import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox
} from '@material-ui/core';
import './FeedbackDialog.css';

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (rating: number, comment?: string, dontAskAgain?: boolean) => void;
}

const labels: Record<number, string> = {
  1: 'Very poor',
  2: 'Poor',
  3: 'Okay',
  4: 'Good',
  5: 'Excellent'
};

const emojis: Record<number, string> = {
  1: '😞',
  2: '☹️',
  3: '😐',
  4: '🙂',
  5: '🤩'
};

const MAX_COMMENT_LEN = 300;

const FeedbackDialog: React.FC<FeedbackDialogProps> = ({
  open,
  onClose,
  onSubmit
}) => {
  const [rating, setRating] = useState<number>(0);
  const [hover, setHover] = useState<number | null>(null);
  const [comment, setComment] = useState<string>('');
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [dontAskAgain, setDontAskAgain] = useState<boolean>(false);

  const handleRatingClick = (value: number) => {
    setRating(value);
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key >= '1' && e.key <= '5') {
        setRating(Number.parseInt(e.key, 10));
      }
      if (e.key === 'ArrowRight') {
        setRating((prev) => Math.min(5, (prev || 0) + 1));
      }
      if (e.key === 'ArrowLeft') {
        setRating((prev) => Math.max(1, (prev || 0) - 1));
      }
    },
    [open]
  );

  useEffect(() => {
    globalThis.addEventListener('keydown', handleKeyDown);
    return () => globalThis.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleSubmit = () => {
    if (rating === 0) {
      // require at least a rating selection
      return;
    }
    const trimmed = comment?.trim() ?? '';
    onSubmit(rating, trimmed || undefined, dontAskAgain);
    setSubmitted(true);
    setTimeout(() => {
      onClose();
    }, 1200);
  };

  const handleLater = () => {
    try {
        onSubmit(0, undefined, false);
      } catch {}
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby='feedback-dialog-title'
      fullWidth
      maxWidth='sm'
    >
      <DialogTitle id='feedback-dialog-title'>Share your feedback</DialogTitle>
      <DialogContent>
        {submitted ? (
          <output className='ep-feedback-thanks' aria-live='polite'>
            <span className='ep-feedback-thanks-emoji' aria-hidden>
              🎉
            </span>
            <span>Thanks for your feedback!</span>
          </output>
        ) : (
          <>
            <div className='ep-feedback-subtitle'>
              How satisfied are you with the application?
            </div>
            <div
              className='ep-feedback-rating-row'
              aria-label='Rate the application from 1 to 5'
            >
              {[1, 2, 3, 4, 5].map((n) => {
                const filled = (hover ?? rating) >= n;
                return (
                  <Button
                    key={n}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(null)}
                    variant={filled ? 'contained' : 'outlined'}
                    color={filled ? 'primary' : 'default'}
                    onClick={() => handleRatingClick(n)}
                    className={`ep-feedback-rating-btn ${
                      filled ? 'filled' : ''
                    } ${hover === n ? 'hover' : ''}`}
                  >
                    {filled ? '★' : '☆'}
                  </Button>
                );
              })}
            </div>
            <div className='ep-feedback-label' aria-live='polite'>
              {hover ?? rating
                ? `${labels[hover ?? rating]} ${emojis[hover ?? rating]}`
                : 'Select a rating'}
            </div>
            <TextField
              label='Your message (Optional)'
              placeholder='Anything we should know?'
              multiline
              minRows={3}
              variant='outlined'
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={comment}
              inputProps={{ maxLength: MAX_COMMENT_LEN }}
              helperText={`${comment.length}/${MAX_COMMENT_LEN}`}
              onChange={(e) => setComment(e.target.value)}
            />
            <div className='ep-feedback-progress' aria-hidden>
              <div
                className='bar'
                style={{
                  width: `${Math.min(
                    100,
                    (comment.length / MAX_COMMENT_LEN) * 100
                  )}%`
                }}
              />
            </div>
          </>
        )}
      </DialogContent>
      {!submitted && (
        <DialogActions>
          <FormControlLabel
            control={
              <Checkbox
                checked={dontAskAgain}
                onChange={(_, checked) => setDontAskAgain(checked)}
                color='primary'
              />
            }
            label="Don't ask again"
          />
          <Button onClick={handleLater}>Later</Button>
          <Button
            onClick={handleSubmit}
            color='primary'
            variant='contained'
            disabled={rating === 0}
          >
            Submit
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default FeedbackDialog;
